import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { RotateCw, X, ZoomIn, ZoomOut } from 'lucide-react';
import type { AqilaGalleryImage } from '@agro1desenvolvimento/agro1-design-system/aqila';
import LightboxNavButton from './lightbox-nav-button';

interface PontoLightboxProps {
  images: AqilaGalleryImage[];
  open: boolean;
  index?: number;
  onOpenChange: (open: boolean) => void;
}

const LENS_SIZE = 180;
const LENS_ZOOM = 2.5;
const VIEWPORT_RATIO = 0.85;
const MOBILE_BREAKPOINT = 640;
const MOBILE_WIDTH_RATIO = 0.94;
const MOBILE_HEIGHT_RATIO = 0.78;

interface LensState {
  visible: boolean;
  screenX: number;
  screenY: number;
  localX: number;
  localY: number;
}

interface NaturalSize {
  width: number;
  height: number;
}

const normalizeRotation = (rotation: number) => ((rotation % 360) + 360) % 360;

const isRotatedSideways = (rotation: number) => {
  const normalized = normalizeRotation(rotation);
  return normalized === 90 || normalized === 270;
};

const toLocalPoint = (
  x: number,
  y: number,
  width: number,
  height: number,
  rotation: number,
) => {
  switch (normalizeRotation(rotation)) {
    case 90:
      return { localX: y, localY: height - x };
    case 180:
      return { localX: width - x, localY: height - y };
    case 270:
      return { localX: width - y, localY: x };
    default:
      return { localX: x, localY: y };
  }
};

const PontoLightbox = ({
  images,
  open,
  index = 0,
  onOpenChange,
}: PontoLightboxProps) => {
  const [current, setCurrent] = useState(index);
  const [rotation, setRotation] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const [naturalSize, setNaturalSize] = useState<NaturalSize | null>(null);
  const [lens, setLens] = useState<LensState>({
    visible: false,
    screenX: 0,
    screenY: 0,
    localX: 0,
    localY: 0,
  });
  const imageRef = useRef<HTMLImageElement>(null);
  const [viewport, setViewport] = useState<NaturalSize | null>(null);

  useEffect(() => {
    const updateViewport = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };
    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  useEffect(() => {
    if (open) {
      setCurrent(index);
      setRotation(0);
      setZoomed(false);
      setNaturalSize(null);
      setLens((previous) => ({ ...previous, visible: false }));
    }
  }, [open, index]);

  if (!open || images.length === 0) return null;

  const total = images.length;
  const image = images[current];
  if (!image) return null;

  const goTo = (next: number) => {
    setCurrent((next + total) % total);
    setRotation(0);
    setZoomed(false);
    setNaturalSize(null);
    setLens((previous) => ({ ...previous, visible: false }));
  };

  const toggleZoom = () => {
    setZoomed((previous) => {
      const next = !previous;
      setLens((current2) => ({ ...current2, visible: false }));
      return next;
    });
  };

  const handlePointerMove = (event: MouseEvent<HTMLDivElement>) => {
    if (!zoomed || !imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
      setLens((previous) => ({ ...previous, visible: false }));
      return;
    }
    const { localX, localY } = toLocalPoint(
      x,
      y,
      imageRef.current.offsetWidth,
      imageRef.current.offsetHeight,
      rotation,
    );
    setLens({ visible: true, screenX: x, screenY: y, localX, localY });
  };

  const handlePointerLeave = () => {
    setLens((previous) => ({ ...previous, visible: false }));
  };

  const sideways = isRotatedSideways(rotation);
  const isMobile = (viewport?.width ?? 0) <= MOBILE_BREAKPOINT;
  const widthRatio = isMobile ? MOBILE_WIDTH_RATIO : VIEWPORT_RATIO;
  const heightRatio = isMobile ? MOBILE_HEIGHT_RATIO : VIEWPORT_RATIO;
  const maxW = (viewport?.width ?? 0) * widthRatio;
  const maxH = (viewport?.height ?? 0) * heightRatio;
  const constraintW = sideways ? maxH : maxW;
  const constraintH = sideways ? maxW : maxH;

  let baseW = constraintW;
  let baseH = constraintH;
  if (naturalSize) {
    const scale = Math.min(
      constraintW / naturalSize.width,
      constraintH / naturalSize.height,
    );
    baseW = naturalSize.width * scale;
    baseH = naturalSize.height * scale;
  }
  const visualW = sideways ? baseH : baseW;
  const visualH = sideways ? baseW : baseH;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center gap-4 bg-black/90 p-6 max-sm:gap-0 max-sm:p-3"
      data-slot="ponto-lightbox"
      role="dialog"
    >
      {total > 1 ? (
        <LightboxNavButton direction="prev" onClick={() => goTo(current - 1)} />
      ) : null}

      <div className="flex flex-col items-center gap-3">
        <div
          className="relative flex items-center justify-center overflow-hidden rounded-aqila-lg bg-white"
          style={{ width: visualW, height: visualH }}
          onMouseLeave={handlePointerLeave}
          onMouseMove={handlePointerMove}
        >
          <div className="absolute left-3 top-3 z-10 flex items-center gap-2">
            <button
              aria-label="Girar"
              className="flex size-9 items-center justify-center rounded-full bg-white text-aqila-body outline-none"
              type="button"
              onClick={() => setRotation((previous) => previous + 90)}
            >
              <RotateCw className="size-4" />
            </button>
            <button
              aria-label={zoomed ? 'Reduzir' : 'Ampliar'}
              aria-pressed={zoomed}
              className={`flex size-9 items-center justify-center rounded-full outline-none${
                zoomed
                  ? ' bg-aqila-primary text-white'
                  : ' bg-white text-aqila-body'
              }`}
              type="button"
              onClick={toggleZoom}
            >
              {zoomed ? (
                <ZoomOut className="size-4" />
              ) : (
                <ZoomIn className="size-4" />
              )}
            </button>
          </div>

          <button
            aria-label="Fechar"
            className="absolute right-3 top-3 z-10 flex size-9 items-center justify-center rounded-full bg-white text-aqila-body outline-none"
            type="button"
            onClick={() => onOpenChange(false)}
          >
            <X className="size-4" />
          </button>

          <img
            ref={imageRef}
            alt={image.alt ?? ''}
            className={`transition-transform${
              zoomed ? ' cursor-crosshair' : ''
            }`}
            src={image.src}
            style={{
              width: baseW,
              height: baseH,
              maxWidth: 'none',
              transform: `rotate(${rotation}deg)`,
            }}
            onLoad={(event) => {
              const target = event.currentTarget;
              setNaturalSize({
                width: target.naturalWidth,
                height: target.naturalHeight,
              });
            }}
          />

          {zoomed && lens.visible && imageRef.current ? (
            <div
              className="pointer-events-none absolute overflow-hidden rounded-full border-2 border-white shadow-lg"
              style={{
                left: lens.screenX - LENS_SIZE / 2,
                top: lens.screenY - LENS_SIZE / 2,
                width: LENS_SIZE,
                height: LENS_SIZE,
              }}
            >
              <img
                alt=""
                src={image.src}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: imageRef.current.offsetWidth,
                  height: imageRef.current.offsetHeight,
                  maxWidth: 'none',
                  transformOrigin: `${lens.localX}px ${lens.localY}px`,
                  transform: `translate(${LENS_SIZE / 2 - lens.localX}px, ${
                    LENS_SIZE / 2 - lens.localY
                  }px) scale(${LENS_ZOOM}) rotate(${rotation}deg)`,
                }}
              />
            </div>
          ) : null}
        </div>

        <span className="rounded-full bg-white px-4 py-1 text-sm font-medium text-aqila-body shadow">
          {current + 1} / {total}
        </span>
      </div>

      {total > 1 ? (
        <LightboxNavButton direction="next" onClick={() => goTo(current + 1)} />
      ) : null}
    </div>
  );
};

export default PontoLightbox;
